import React from "react";
import OnboardingScreen, { OnboardingErrorText, OnboardingTitle } from "../OnboardingScreen";
import { OnboardingGlassButton, OnboardingNextButton } from "../OnboardingButtons";
import PeakTextField from "../PeakTextField";
import { AMBASSADOR_COPY } from "../onboardingCopy";

interface AmbassadorCodeStepProps {
  code: string;
  /** Set once the code is validated. */
  ambassadorFirstName: string | null;
  canContinue: boolean;
  checking?: boolean;
  error?: string | null;
  onChange: (value: string) => void;
  onBack: () => void;
  /** Validates the code, or continues once it is validated. */
  onApply: () => void;
  onSkip: () => void;
}

const AmbassadorCodeStep: React.FC<AmbassadorCodeStepProps> = ({
  code,
  ambassadorFirstName,
  canContinue,
  checking = false,
  error = null,
  onChange,
  onBack,
  onApply,
  onSkip,
}) => {
  const validated = ambassadorFirstName !== null;
  const label = validated ? "Continue" : checking ? AMBASSADOR_COPY.checking : AMBASSADOR_COPY.apply;

  return (
    <OnboardingScreen
      onBack={onBack}
      footer={
        <div className="flex flex-col gap-3">
          <OnboardingNextButton label={label} enabled={canContinue && !checking} onClick={onApply} />
          {!validated && <OnboardingGlassButton label={AMBASSADOR_COPY.skip} enabled={!checking} onClick={onSkip} />}
        </div>
      }
    >
      <div className="flex flex-col gap-7">
        <OnboardingTitle title={AMBASSADOR_COPY.title} subtitle={AMBASSADOR_COPY.subtitle} />
        <div className="flex flex-col gap-3">
          <PeakTextField
            label="Ambassador code"
            value={code}
            onChange={onChange}
            placeholder={AMBASSADOR_COPY.placeholder}
            autoCapitalize="characters"
            autoComplete="off"
            error={Boolean(error)}
            onSubmit={() => canContinue && !checking && onApply()}
          />
          <OnboardingErrorText>{error}</OnboardingErrorText>
          {validated && (
            <div
              className="flex items-start gap-3 rounded-xl border-2 border-peak-brand bg-peak-brand-subtle px-4 py-3.5"
              role="status"
            >
              <span className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-peak-brand" aria-hidden="true">
                <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12.5l4.5 4.5L19 7" />
                </svg>
              </span>
              <p className="font-funnel text-[15px] leading-snug text-peak-text">
                {AMBASSADOR_COPY.thanks(ambassadorFirstName)}
              </p>
            </div>
          )}
        </div>
      </div>
    </OnboardingScreen>
  );
};

export default AmbassadorCodeStep;
